import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Paginated } from '@marketing-os/shared';
import { apiGet, apiPost } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import type { ApprovalRequest } from '@/types/api';

export interface ApprovalListParams {
  status?: string;
  clientId?: string;
  page?: number;
  pageSize?: number;
}

export function useApprovals(params: ApprovalListParams = {}) {
  return useQuery({
    queryKey: qk.approvals(params),
    queryFn: () => apiGet<Paginated<ApprovalRequest>>('/approvals', { params }),
  });
}

export function useApproval(id: string | undefined) {
  return useQuery({
    queryKey: qk.approval(id ?? ''),
    queryFn: () => apiGet<ApprovalRequest>(`/approvals/${id}`),
    enabled: Boolean(id),
  });
}

export function usePendingApprovalsCount() {
  return useQuery({
    queryKey: qk.approvalsPending,
    queryFn: () => apiGet<{ count: number }>('/approvals/pending-count'),
    refetchInterval: 120_000,
  });
}

export function useDecideApproval() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approved, note }: { id: string; approved: boolean; note?: string }) =>
      apiPost<ApprovalRequest>(`/approvals/${id}/decide`, { approved, note }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['approvals'] });
      void client.invalidateQueries({ queryKey: ['campaign-projects'] });
      void client.invalidateQueries({ queryKey: ['social'] });
    },
  });
}
